import { Bell, Check, CheckCheck } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { toast } from "@/shared/hooks/use-toast";
import { useDateFormat } from "@/shared/hooks/use-date-format";
import { useLanguage } from "@/shared/hooks/use-language";
import type { DateFormat } from "@/shared/lib/date-format";
import { formatDate } from "@/shared/lib/date-utils";
import { ScopeContextStorage } from "@/shared/lib/scope-storage";

import {
  getNotificationMessages,
  notificationMessageQueryKeys,
  patchNotificationMessages,
  type NotificationMessage,
} from "../api/notification-message";

export const NotificationBell = () => {
  const queryClient = useQueryClient();
  const { language } = useLanguage();
  const { dateFormat } = useDateFormat();
  const sessionId = ScopeContextStorage.getCurrentSessionId();
  const scopeContext = sessionId ? ScopeContextStorage.getScopeContext(sessionId) : null;

  const query = useQuery({
    queryKey: notificationMessageQueryKeys.processing(sessionId),
    queryFn: () => getNotificationMessages(sessionId!, scopeContext!, { status: "processing", limit: 20 }),
    enabled: !!sessionId && !!scopeContext,
  });

  const messages = query.data?.messages ?? [];
  const count = messages.length;
  const display = count > 99 ? "99+" : String(count);

  const markAsSuccess = useMutation({
    mutationFn: async (ids: string[]) => {
      if (!sessionId || !scopeContext || ids.length === 0) return;
      return patchNotificationMessages(
        sessionId,
        scopeContext,
        ids.map((id) => ({ id, status: "success" })),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationMessageQueryKeys.all });
    },
    onError: (err) => {
      toast({
        title: language === "pt" ? "Erro" : "Error",
        description: err instanceof Error ? err.message : "Failed to update notification",
        variant: "destructive",
      });
    },
  });

  const headerLabel = language === "pt" ? "Notificações" : "Notifications";
  const emptyLabel = language === "pt" ? "Nenhuma notificação nova" : "No new notifications";
  const markAllLabel = language === "pt" ? "Marcar todas como lidas" : "Mark all as read";
  const markOneLabel = language === "pt" ? "Marcar como lida" : "Mark as read";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="relative inline-flex h-10 w-10 items-center justify-center rounded-md transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
          aria-label={count ? `${headerLabel}: ${count}` : headerLabel}
        >
          <Bell className="h-5 w-5 text-foreground" />
          {count > 0 && (
            <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground">
              {display}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" sideOffset={8}>
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <p className="text-sm font-semibold">{headerLabel}</p>
          {count > 0 && (
            <button
              type="button"
              onClick={() => markAsSuccess.mutate(messages.map((m) => m.id))}
              disabled={markAsSuccess.isPending}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              <CheckCheck className="h-3 w-3" />
              {markAllLabel}
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {messages.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground">
              {emptyLabel}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {messages.map((message) => (
                <NotificationItem
                  key={message.id}
                  message={message}
                  language={language}
                  dateFormat={dateFormat}
                  markLabel={markOneLabel}
                  disabled={markAsSuccess.isPending}
                  onMark={() => markAsSuccess.mutate([message.id])}
                />
              ))}
            </ul>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface NotificationItemProps {
  message: NotificationMessage;
  language: "pt" | "en";
  dateFormat: DateFormat;
  markLabel: string;
  disabled: boolean;
  onMark: () => void;
}

const NotificationItem = ({ message, language, dateFormat, markLabel, disabled, onMark }: NotificationItemProps) => (
  <li className="flex items-start gap-2 px-4 py-3 transition-colors hover:bg-muted/40">
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-medium text-foreground">{message.title}</p>
      <p className="truncate text-xs text-muted-foreground">{message.description}</p>
      <p className="mt-1 text-[11px] text-muted-foreground">
        {formatRelative(message.created, language, dateFormat)}
      </p>
    </div>
    <button
      type="button"
      onClick={onMark}
      disabled={disabled}
      aria-label={markLabel}
      title={markLabel}
      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
    >
      <Check className="h-4 w-4" />
    </button>
  </li>
);

// Under 24 hours, a notification's age is a distance from now, not a date, so it stays
// relative regardless of the format preference. From 24 hours on there is no "now" left to
// be a distance from, so this reads as a date like every other timestamp in the console —
// through the same preference, not a locale picked from the language toggle.
function formatRelative(iso: string, language: "pt" | "en", dateFormat: DateFormat): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
  const pt = language === "pt";
  if (diffSec < 60) return pt ? "agora" : "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return pt ? `${diffMin}m atrás` : `${diffMin}m ago`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return pt ? `${diffHour}h atrás` : `${diffHour}h ago`;
  return formatDate(date, dateFormat);
}
